import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';
import { AddressComponents } from '../../../utils/AddressUtils';
import SignupFooter from '../SignupFooter';
import {
  Entity,
  VendorType,
  VendorTypeDisplayNames,
} from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import HoursOfOperationWidget from './widgets/HoursOfOperationsWidget';
import dayjs from 'dayjs';
import { patchVendor } from './VendorSignupApis';
import { InputNumber, Select } from 'antd';
import FlexibleAddressInput from '../customers/FlexibleAddressInput';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';

interface VendorBusinessInfoProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (newStep: string) => void;
}

const defaultHoursOfOperation = {
  Monday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Tuesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Wednesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Thursday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Friday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Saturday: { isOpen: false, from: '', to: '' },
  Sunday: { isOpen: false, from: '', to: '' },
};
export type HoursOfOperation = {
  isOpen: boolean;
  from: string | dayjs.Dayjs;
  to: string | dayjs.Dayjs;
};

export type VendorBusinessInfoForm = {
  businessName: string;
  businessPhone: string;
  businessAddress: string;
  businessEmail: string;
  websiteUrl: string;
  descriptionOfServices: string;
  yearsInBusiness: number;
  employerIdNumber: string;
  primary_vendor_type: VendorType;
  secondary_vendor_types: VendorType[];
  hoursOfOperation: {
    Monday: HoursOfOperation;
    Tuesday: HoursOfOperation;
    Wednesday: HoursOfOperation;
    Thursday: HoursOfOperation;
    Friday: HoursOfOperation;
    Saturday: HoursOfOperation;
    Sunday: HoursOfOperation;
  };
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  termsAccepted?: boolean;
};

function VendorBusinessInfo({
  currStep,
  selectedRole,
  setStep,
}: VendorBusinessInfoProps) {
  const { userId } = useUser();
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<VendorBusinessInfoForm>({
    defaultValues: {
      hoursOfOperation: defaultHoursOfOperation,
      businessName: '',
      businessPhone: '',
      businessAddress: '',
      businessEmail: '',
      websiteUrl: '',
      yearsInBusiness: 0,
      employerIdNumber: '',
      primary_vendor_type: VendorType.RepairShop,
      secondary_vendor_types: [],
    },
  });
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [addressFields, setAddressFields] = useState<AddressComponents>({});
  const hoursOfOperation = watch('hoursOfOperation');
  const termsAccepted = watch('termsAccepted');
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);

  const onSubmit: SubmitHandler<VendorBusinessInfoForm> = (data) => {
    // Convert vendor_type array to comma-separated string for API
    safeSessionSetItem('vendorBusinessInfo', JSON.stringify(data));
    patchVendor(
      data,
      userId ?? '',
      callProtectedApi,
      handleSetLoading,
      setStep,
      'vendor_personal_info'
    );
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  useEffect(() => {
    for (const key in addressFields) {
      if (addressFields.hasOwnProperty(key)) {
        setValue(
          key as keyof VendorBusinessInfoForm,
          addressFields[key as keyof AddressComponents]
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFields]);

  useEffect(() => {
    // Retrieve form data from session storage
    const savedData = safeSessionGetItem('vendorBusinessInfo');
    if (savedData) {
      const parsedData: VendorBusinessInfoForm = JSON.parse(savedData);
      // Set form data using setValue
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof VendorBusinessInfoForm,
            parsedData[key as keyof VendorBusinessInfoForm]
          );
        }
      }
      if (parsedData?.address_city?.length) {
        setAddressSelectedFromDropdown(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            <style>
              {`
                   .pac-container {
                     box-shadow: none !important;
                     border: 1px solid #ddd; /* Optional: Add a light border */
                   }
                 `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-[60%] flex-col items-center justify-center space-y-3`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-[60%] space-y-4 pb-[10%]`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessName"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Name
                        </label>
                        <input
                          type="text"
                          placeholder="Truck Service Depot"
                          {...register('businessName', {
                            required: 'Business name is required',
                          })}
                          id="businessName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="businessEmail"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Email
                        </label>
                        <input
                          type="email"
                          placeholder="info@truckservicedepot.com"
                          {...register('businessEmail', {
                            required: 'Business email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                          })}
                          id="businessEmail"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessEmail && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessEmail.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-full">
                        <label
                          htmlFor="businessPhone"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Phone
                        </label>
                        <input
                          type="tel"
                          placeholder="8004360832"
                          {...register('businessPhone', {
                            required: 'Business phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                            onChange: (e) => {
                              // Automatically format the input to start with +1
                              if (!e.target.value.startsWith('+1')) {
                                e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                              }
                            },
                          })}
                          id="businessPhone"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessPhone && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessPhone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessAddress"
                          className="block text-[16px] text-graysecondary"
                        >
                          First Location Business Address
                        </label>
                        <FlexibleAddressInput
                          searchFieldName="businessAddress"
                          streetNumberFieldName="address_st_number"
                          streetNameFieldName="address_st_name"
                          unitFieldName="address_unit"
                          cityFieldName="address_city"
                          stateFieldName="address_state"
                          zipFieldName="address_zip"
                          register={register}
                          control={control}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          initialMode="autocomplete"
                          searchPlaceholder="Enter business address"
                          onAddressComponentsChange={setAddressFields}
                          addressSelectedFromDropdown={
                            addressSelectedFromDropdown
                          }
                          setAddressSelectedFromDropdown={
                            setAddressSelectedFromDropdown
                          }
                          cookieKey="businessAddressVendorMode"
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="websiteUrl"
                          className="block text-[16px] text-graysecondary"
                        >
                          Website URL
                        </label>
                        <input
                          type="text"
                          placeholder="https://truckservicedepot.com"
                          {...register('websiteUrl', {
                            required: 'Website URL is required',
                          })}
                          id="websiteUrl"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.websiteUrl ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.websiteUrl && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.websiteUrl.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="yearsInBusiness"
                          className="block text-[16px] text-graysecondary"
                        >
                          Years in Business
                        </label>
                        <InputNumber
                          id="yearsInBusiness"
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e') {
                              e.preventDefault();
                            }
                          }}
                          min={0}
                          placeholder="5"
                          className="mt-1 flex h-[38px] w-full items-center"
                          onChange={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                          onStep={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                        />
                        {errors.yearsInBusiness && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.yearsInBusiness.message}
                          </p>
                        )}
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="employerIdNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          Employer ID Number (EIN)
                        </label>
                        <input
                          type="text"
                          placeholder="123456789"
                          maxLength={9}
                          {...register('employerIdNumber', {
                            required: 'Employer ID Number is required',
                            pattern: {
                              value: /^[0-9]{9}$/,
                              message: 'EIN must be exactly 9 digits',
                            },
                            onChange: (e) => {
                              // Only allow digits
                              e.target.value = e.target.value.replace(
                                /[^0-9]/g,
                                ''
                              );
                            },
                          })}
                          id="employerIdNumber"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.employerIdNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.employerIdNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.employerIdNumber.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="primary_vendor_type"
                          className="block text-[16px] text-graysecondary"
                        >
                          Primary Service Offered
                        </label>
                        <Select
                          id="primary_vendor_type"
                          className="mt-1 w-full"
                          value={watch('primary_vendor_type')}
                          onChange={(value) =>
                            setValue('primary_vendor_type', value)
                          }
                          options={Object.values(VendorType).map((type) => ({
                            value: type,
                            label: VendorTypeDisplayNames[type],
                          }))}
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="vendor_type"
                          className="block text-[16px] text-graysecondary"
                        >
                          Other Services Offered
                        </label>
                        <Select
                          id="secondary_vendor_types"
                          mode="multiple"
                          placeholder="Select service types"
                          className="mt-1 w-full"
                          value={watch('secondary_vendor_types')}
                          onChange={(value) =>
                            setValue('secondary_vendor_types', value)
                          }
                          options={Object.values(VendorType).map((type) => ({
                            value: type,
                            label: VendorTypeDisplayNames[type],
                          }))}
                        />
                      </div>
                    </div>

                    <HoursOfOperationWidget
                      control={control}
                      hoursOfOperation={hoursOfOperation}
                    />

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <textarea
                        {...register('descriptionOfServices', {
                          required: 'Description of services is required',
                        })}
                        id="descriptionOfServices"
                        placeholder="Describe the services you offer"
                        className={`mt-1 block min-h-[150px] w-full border px-3 py-2 ${
                          errors.descriptionOfServices
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>
                    <TermsAndConditionsCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
      case 'tablet':
        return (
          <>
            <style>
              {`
                 .pac-container {
                   box-shadow: none !important;
                   border: 1px solid #ddd; /* Optional: Add a light border */
                 }
               `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-full flex-col items-center justify-center space-y-3 px-4`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-full space-y-4 px-4 pb-[10%]`}
                  >
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessName"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Name
                        </label>
                        <input
                          type="text"
                          placeholder="Truck Service Depot"
                          {...register('businessName', {
                            required: 'Business name is required',
                          })}
                          id="businessName"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessName.message}
                          </p>
                        )}
                      </div>

                      <div className="w-[50%]">
                        <label
                          htmlFor="businessEmail"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Email
                        </label>
                        <input
                          type="email"
                          placeholder="info@truckservicedepot.com"
                          {...register('businessEmail', {
                            required: 'Business email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                          })}
                          id="businessEmail"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessEmail && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessEmail.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-full">
                        <label
                          htmlFor="businessPhone"
                          className="block text-[16px] text-graysecondary"
                        >
                          Business Phone
                        </label>
                        <input
                          type="tel"
                          placeholder="8004360832"
                          {...register('businessPhone', {
                            required: 'Business phone number is required',
                            pattern: {
                              value: /^\+1[0-9]{10}$/,
                              message:
                                'Phone number must be in the format +1XXXXXXXXXX',
                            },
                            onChange: (e) => {
                              // Automatically format the input to start with +1
                              if (!e.target.value.startsWith('+1')) {
                                e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                              }
                            },
                          })}
                          id="businessPhone"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.businessPhone && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.businessPhone.message}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="businessAddress"
                          className="block text-[16px] text-graysecondary"
                        >
                          First Location Business Address
                        </label>

                        <FlexibleAddressInput
                          searchFieldName="businessAddress"
                          streetNumberFieldName="address_st_number"
                          streetNameFieldName="address_st_name"
                          unitFieldName="address_unit"
                          cityFieldName="address_city"
                          stateFieldName="address_state"
                          zipFieldName="address_zip"
                          register={register}
                          control={control}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                          initialMode="autocomplete"
                          searchPlaceholder="Enter business address"
                          onAddressComponentsChange={setAddressFields}
                          addressSelectedFromDropdown={
                            addressSelectedFromDropdown
                          }
                          setAddressSelectedFromDropdown={
                            setAddressSelectedFromDropdown
                          }
                          cookieKey="businessAddressVendorMode"
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="websiteUrl"
                          className="block text-[16px] text-graysecondary"
                        >
                          Website URL
                        </label>
                        <input
                          type="text"
                          placeholder="https://truckservicedepot.com"
                          {...register('websiteUrl', {
                            required: 'Website URL is required',
                          })}
                          id="websiteUrl"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.websiteUrl ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.websiteUrl && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.websiteUrl.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="yearsInBusiness"
                          className="block text-[16px] text-graysecondary"
                        >
                          Years in Business
                        </label>
                        <InputNumber
                          id="yearsInBusiness"
                          min={0}
                          placeholder="5"
                          className="mt-1 flex h-[38px] w-full items-center"
                          onChange={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                          onStep={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                        />
                        {errors.yearsInBusiness && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.yearsInBusiness.message}
                          </p>
                        )}
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="employerIdNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          Employer ID Number (EIN)
                        </label>
                        <input
                          type="text"
                          placeholder="123456789"
                          maxLength={9}
                          {...register('employerIdNumber', {
                            required: 'Employer ID Number is required',
                            pattern: {
                              value: /^[0-9]{9}$/,
                              message: 'EIN must be exactly 9 digits',
                            },
                            onChange: (e) => {
                              // Only allow digits
                              e.target.value = e.target.value.replace(
                                /[^0-9]/g,
                                ''
                              );
                            },
                          })}
                          id="employerIdNumber"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.employerIdNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.employerIdNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.employerIdNumber.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="primary_vendor_type"
                          className="block text-[16px] text-graysecondary"
                        >
                          Primary Service Offered
                        </label>
                        <Select
                          id="primary_vendor_type"
                          className="mt-1 w-full"
                          value={watch('primary_vendor_type')}
                          onChange={(value) =>
                            setValue('primary_vendor_type', value)
                          }
                          options={Object.values(VendorType).map((type) => ({
                            value: type,
                            label: VendorTypeDisplayNames[type],
                          }))}
                        />
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="vendor_type"
                          className="block text-[16px] text-graysecondary"
                        >
                          Other Services Offered
                        </label>
                        <Select
                          id="secondary_vendor_types"
                          mode="multiple"
                          placeholder="Select service types"
                          className="mt-1 w-full"
                          value={watch('secondary_vendor_types')}
                          onChange={(value) =>
                            setValue('secondary_vendor_types', value)
                          }
                          options={Object.values(VendorType).map((type) => ({
                            value: type,
                            label: VendorTypeDisplayNames[type],
                          }))}
                        />
                      </div>
                    </div>

                    <HoursOfOperationWidget
                      control={control}
                      hoursOfOperation={hoursOfOperation}
                    />

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <textarea
                        {...register('descriptionOfServices', {
                          required: 'Description of services is required',
                        })}
                        id="descriptionOfServices"
                        placeholder="Describe the services you offer"
                        className={`mt-1 block min-h-[150px] w-full border px-3 py-2 ${
                          errors.descriptionOfServices
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>
                    <TermsAndConditionsCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
      case 'mobile':
        return (
          <>
            <style>
              {`
                 .pac-container {
                   box-shadow: none !important;
                   border: 1px solid #ddd; /* Optional: Add a light border */
                 }
               `}
            </style>
            <div
              className={`flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px]`}
            >
              {!loading ? (
                <>
                  <div
                    className={`flex w-full flex-col items-center justify-center space-y-3`}
                  >
                    <h2 className="text-center text-[30px] font-bold">
                      Business Information
                    </h2>
                    <p className="text-[16px] text-graysecondary">
                      Provide your business details
                    </p>
                  </div>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={`w-full space-y-4 pb-[10%]`}
                  >
                    <div>
                      <label
                        htmlFor="businessName"
                        className="block text-[16px] text-graysecondary"
                      >
                        Business Name
                      </label>
                      <input
                        type="text"
                        placeholder="Truck Service Depot"
                        {...register('businessName', {
                          required: 'Business name is required',
                        })}
                        id="businessName"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.businessName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.businessName && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.businessName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="businessEmail"
                        className="block text-[16px] text-graysecondary"
                      >
                        Business Email
                      </label>
                      <input
                        type="email"
                        placeholder="info@truckservicedepot.com"
                        {...register('businessEmail', {
                          required: 'Business email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        id="businessEmail"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.businessEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.businessEmail && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.businessEmail.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="businessPhone"
                        className="block text-[16px] text-graysecondary"
                      >
                        Business Phone
                      </label>
                      <input
                        type="tel"
                        placeholder="8004360832"
                        {...register('businessPhone', {
                          required: 'Business phone number is required',
                          pattern: {
                            value: /^\+1[0-9]{10}$/,
                            message:
                              'Phone number must be in the format +1XXXXXXXXXX',
                          },
                          onChange: (e) => {
                            // Automatically format the input to start with +1
                            if (!e.target.value.startsWith('+1')) {
                              e.target.value = `+1${e.target.value.replace(/^\+1|[^0-9]/g, '')}`;
                            }
                          },
                        })}
                        id="businessPhone"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.businessPhone && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.businessPhone.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="businessAddress"
                        className="block text-[16px] text-graysecondary"
                      >
                        First Location Business Address
                      </label>
                      <FlexibleAddressInput
                        searchFieldName="businessAddress"
                        streetNumberFieldName="address_st_number"
                        streetNameFieldName="address_st_name"
                        unitFieldName="address_unit"
                        cityFieldName="address_city"
                        stateFieldName="address_state"
                        zipFieldName="address_zip"
                        register={register}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        errors={errors}
                        initialMode="autocomplete"
                        searchPlaceholder="Enter business address"
                        onAddressComponentsChange={setAddressFields}
                        addressSelectedFromDropdown={
                          addressSelectedFromDropdown
                        }
                        setAddressSelectedFromDropdown={
                          setAddressSelectedFromDropdown
                        }
                        cookieKey="businessAddressVendorMode"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="websiteUrl"
                        className="block text-[16px] text-graysecondary"
                      >
                        Website URL
                      </label>
                      <input
                        type="text"
                        placeholder="https://truckservicedepot.com"
                        {...register('websiteUrl', {
                          required: 'Website URL is required',
                        })}
                        id="websiteUrl"
                        className={`mt-1 block w-full border px-3 py-2 ${errors.websiteUrl ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.websiteUrl && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.websiteUrl.message}
                        </p>
                      )}
                    </div>

                    <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                      <div className="w-[50%]">
                        <label
                          htmlFor="yearsInBusiness"
                          className="block text-[16px] text-graysecondary"
                        >
                          Years in Business
                        </label>
                        <InputNumber
                          id="yearsInBusiness"
                          min={0}
                          placeholder="5"
                          className="mt-1 flex h-[38px] w-full items-center"
                          onChange={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                          onStep={(value) =>
                            setValue('yearsInBusiness', value as number)
                          }
                        />
                        {errors.yearsInBusiness && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.yearsInBusiness.message}
                          </p>
                        )}
                      </div>
                      <div className="w-[50%]">
                        <label
                          htmlFor="employerIdNumber"
                          className="block text-[16px] text-graysecondary"
                        >
                          Employer ID Number (EIN)
                        </label>
                        <input
                          type="text"
                          placeholder="123456789"
                          maxLength={9}
                          {...register('employerIdNumber', {
                            required: 'Employer ID Number is required',
                            pattern: {
                              value: /^[0-9]{9}$/,
                              message: 'EIN must be exactly 9 digits',
                            },
                            onChange: (e) => {
                              // Only allow digits
                              e.target.value = e.target.value.replace(
                                /[^0-9]/g,
                                ''
                              );
                            },
                          })}
                          id="employerIdNumber"
                          className={`mt-1 block w-full border px-3 py-2 ${errors.employerIdNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                        />
                        {errors.employerIdNumber && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.employerIdNumber.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="primary_vendor_type"
                        className="block text-[16px] text-graysecondary"
                      >
                        Primary Service Offered
                      </label>
                      <Select
                        id="primary_vendor_type"
                        className="mt-1 w-full"
                        value={watch('primary_vendor_type')}
                        onChange={(value) =>
                          setValue('primary_vendor_type', value)
                        }
                        options={Object.values(VendorType).map((type) => ({
                          value: type,
                          label: VendorTypeDisplayNames[type],
                        }))}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="vendor_type"
                        className="block text-[16px] text-graysecondary"
                      >
                        Other Services Offered
                      </label>
                      <Select
                        id="secondary_vendor_types"
                        mode="multiple"
                        placeholder="Select service types"
                        className="mt-1 w-full"
                        value={watch('secondary_vendor_types')}
                        onChange={(value) =>
                          setValue('secondary_vendor_types', value)
                        }
                        options={Object.values(VendorType).map((type) => ({
                          value: type,
                          label: VendorTypeDisplayNames[type],
                        }))}
                      />
                    </div>

                    <HoursOfOperationWidget
                      control={control}
                      hoursOfOperation={hoursOfOperation}
                    />

                    <div className="w-full">
                      <label
                        htmlFor="descriptionOfServices"
                        className="block text-[16px] text-graysecondary"
                      >
                        Description of Services
                      </label>
                      <textarea
                        {...register('descriptionOfServices', {
                          required: 'Description of services is required',
                        })}
                        id="descriptionOfServices"
                        placeholder="Describe the services you offer"
                        className={`mt-1 block min-h-[150px] w-full border px-3 py-2 ${
                          errors.descriptionOfServices
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                      />
                      {errors.descriptionOfServices && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.descriptionOfServices.message}
                        </p>
                      )}
                    </div>
                    <TermsAndConditionsCheckboxVendor
                      register={register}
                      errors={errors}
                    />
                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      disableSubmit={!termsAccepted}
                    />
                  </form>
                </>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              )}
            </div>
          </>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default VendorBusinessInfo;

const TermsAndConditionsCheckboxVendor = ({
  register,
  errors,
}: {
  register: any;
  errors: any;
}) => (
  <div className="mt-4">
    <div className="flex flex-row items-center">
      <div className="flex h-5 items-center">
        <input
          id="termsAccepted"
          type="checkbox"
          {...register('termsAccepted', {
            required: 'You must accept the terms and conditions to continue',
          })}
          className="h-8 w-8 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>
      <div className="ml-3 text-sm">
        <label
          htmlFor="termsAccepted"
          className="text-[16px] font-medium text-gray-700"
        >
          I accept the terms and conditions required for applying to be a
          merchant member of the CB Charge program with Corporate Billing, LLC
        </label>
        <p className="mt-2 text-[14px] font-medium text-gray-700">
          By checking this box, you acknowledge that you have read, understood,
          and agree to be bound by these{' '}
          <a
            href="https://prod-public-images-m6zzcoyk.s3.us-east-2.amazonaws.com/cbCharge-Merchant-application-20240719.pdf"
            target="_blank"
            rel="noreferrer noopener"
            className="text-blue-400 underline"
          >
            terms and conditions
          </a>
          .
        </p>
      </div>
    </div>
    {errors.termsAccepted && (
      <p className="mt-1 text-sm text-red-500">
        {errors.termsAccepted.message}
      </p>
    )}
  </div>
);
